/**
 * PrivacyGlanceWidget — public, anonymised platform-wide privacy stats.
 *
 *   • Redactions in the last 30 days
 *   • % of LLM calls that ran on Bedrock (inside operator's AWS)
 *   • Active tenants
 *   • Active BYOC connections
 *   • 14-day daily redaction sparkline (when ?include=sparkline)
 *
 * Source: GET /api/privacy/public-stats?include=sparkline (no auth,
 * cached hourly). Used on the EcosystemGateway hero + on /security as
 * social proof.
 *
 * Layout: mobile-first vertical stack on narrow viewports, 4-column
 * grid on >= sm (640px). Each tile uses min-w-0 + tight labels so the
 * 0.22em tracked text never overflows into adjacent tiles.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Activity, Cloud, Building2 } from 'lucide-react';
import api from '@/lib/api';
import CountUp from './CountUp';

function fmt(n) {
  if (typeof n !== 'number') return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

export default function PrivacyGlanceWidget({ compact = false }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get('/privacy/public-stats?include=sparkline')
      .then(({ data }) => { if (!cancel) { setStats(data); setLoading(false); } })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading || !stats) return null;

  const tiles = [
    { icon: ShieldCheck, label: 'Redactions',  short: 'Redact',    raw: stats.redactions_30d,         format: fmt,                                   sub: 'masked · 30d',                                color: 'text-emerald-300',                       border: 'border-emerald-400/40 bg-emerald-500/5' },
    { icon: Activity,    label: 'Sovereign',   short: 'Sovereign', raw: stats.sovereignty_pct_30d,    format: (v) => `${Math.round(v)}%`,             sub: `of ${fmt(stats.llm_calls_30d)} calls`,        color: 'text-[var(--brand-primary)]',            border: 'border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)]' },
    { icon: Cloud,       label: 'Live BYOC',   short: 'BYOC',      raw: stats.aws_connections_active, format: fmt,                                   sub: 'AWS · connected',                              color: 'text-amber-300',                         border: 'border-amber-400/40 bg-amber-500/5' },
    { icon: Building2,   label: 'Tenants',     short: 'Tenants',   raw: stats.tenants_active,         format: fmt,                                   sub: 'sovereign rooms',                              color: 'text-sky-300',                           border: 'border-sky-400/40 bg-sky-500/5' },
  ];

  const sparkline = stats.sparkline_14d || [];
  const sparkMax = Math.max(1, ...sparkline.map((d) => d.redactions || 0));

  return (
    <div data-testid="privacy-glance-widget" className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-60 flex items-center justify-between flex-wrap gap-2">
        <span>Privacy at a glance · last 30 days</span>
        <span className="pal-mono opacity-50 normal-case tracking-normal text-[10px]">refreshes hourly</span>
      </div>

      <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {tiles.map((t) => (
          <div key={t.label} data-testid={`privacy-glance-${t.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`min-w-0 p-3 rounded border ${t.border}`}>
            <div className={`flex items-center gap-1.5 min-w-0 ${t.color}`}>
              <t.icon size={11} className="shrink-0" />
              <span className="text-[9px] uppercase tracking-[0.14em] font-bold truncate">{t.short}</span>
            </div>
            <div className={`pal-mono text-lg font-bold mt-1 ${t.color} truncate`}>
              <CountUp end={t.raw} format={t.format} />
            </div>
            <div className="text-[9px] opacity-55 mt-0.5 truncate">{t.sub}</div>
          </div>
        ))}
      </div>

      {sparkline.length > 0 && (
        <div className="rounded border border-emerald-400/15 bg-emerald-500/[0.03] p-3" data-testid="privacy-glance-sparkline">
          <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-emerald-300/80 mb-2 flex items-center justify-between">
            <span>14-day redactions trend</span>
            <span className="pal-mono opacity-60 normal-case tracking-normal">{sparkMax} peak</span>
          </div>
          <div className="flex items-end gap-0.5 h-10">
            {sparkline.map((d) => {
              const h = d.redactions ? Math.max(3, (d.redactions / sparkMax) * 40) : 2;
              return (
                <div key={d.day} className="flex-1 rounded-t" title={`${d.day} · ${d.redactions} redaction(s)`}
                  style={{
                    height: `${h}px`,
                    background: d.redactions === 0 ? 'rgba(52,211,153,0.10)' : '#34D399',
                  }}/>
              );
            })}
          </div>
          <div className="mt-1 flex justify-between text-[9px] pal-mono opacity-45">
            <span>{sparkline[0]?.day || ''}</span>
            <span>today</span>
          </div>
        </div>
      )}
    </div>
  );
}
